import React, { useState, useEffect } from 'react';
import { 
  ShieldCheck, User, Building2, Camera, FileText, MapPin, 
  Landmark, Phone, CheckCircle2, X, ChevronRight, Wallet, 
  Plane, Globe, Lock, Unlock, Gavel, PenTool, Loader2,
  TrendingUp, Target, CreditCard, HardDrive, Calculator, Info, ScrollText
} from 'lucide-react';

/**
 * 🛰️ SAHELGUARD SOVEREIGN COMPLIANCE SYSTEM - FULL INTEGRATION V10.5
 * Last Update: 19/03/2026 | Jurisdiction: Burkina Faso (UEMOA)
 * Includes: Full Contract, Article 7 Productivity, KYC & PawaPay Flow
 */

const SahelGuardPortal = () => {
  const [view, setView] = useState('landing');
  const [step, setStep] = useState('selection');
  const [accountType, setAccountType] = useState('INDIVIDUEL');
  const [showFullContract, setShowFullContract] = useState(false);

  const [formData, setFormData] = useState({
    nom_gerant: '', cni_numero: '', cni_exp: '', tel_perso: '',
    raison_sociale: '', rccm: '', ifu: '', 
    ville: '', quartier: '', gps_link: '',
    grade: 'POINT FOCAL', payment_phone: '',
    files: {}
  });

  const GRADES = {
    'LEGIONNAIRE': { caution: 100000, welcome: 0, vMin: 0, comm: 0.15 },
    'POINT FOCAL': { caution: 250000, welcome: 0.02, vMin: 0, comm: 0.15 },
    'AMBASSADEUR': { caution: 500000, welcome: 0.05, vMin: 250000, comm: 0.15 },
    'GOUVERNEUR': { caution: 1500000, welcome: 0.10, vMin: 500000, comm: 0.15 }
  };

  const updateForm = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));

  return (
    <div className="min-h-screen bg-[#050505] text-white font-sans selection:bg-emerald-500/30">
      
      {/* NAVIGATION HEADER */}
      <nav className="flex justify-between items-center px-8 py-10 max-w-7xl mx-auto">
        <div className="flex items-center gap-3">
          <div className="bg-emerald-500 p-2.5 rounded-2xl shadow-[0_0_30px_rgba(16,185,129,0.2)]">
            <ShieldCheck size={32} className="text-black" />
          </div>
          <div className="flex flex-col">
            <span className="text-2xl font-black italic tracking-tighter leading-none">SAHELGUARD</span>
            <span className="text-[8px] font-black uppercase tracking-[0.4em] text-emerald-500/60 mt-1">Sovereign Cloud Net</span>
          </div>
        </div>
        <button 
          onClick={() => setView('recruit_flow')}
          className="bg-white/5 border border-white/10 px-8 py-4 rounded-full font-black text-[10px] uppercase tracking-widest hover:bg-emerald-500 hover:text-black transition-all">
          Devenir Agent Agréé
        </button>
      </nav>

      {/* MODAL DE RECRUTEMENT */}
      {view === 'recruit_flow' && (
        <div className="fixed inset-0 z-[5000] bg-black/95 backdrop-blur-3xl overflow-y-auto flex items-start justify-center p-4">
          <div className="w-full max-w-5xl bg-[#0a0a0a] border border-white/10 rounded-[4rem] my-10 p-8 md:p-16 relative shadow-2xl">
            <button onClick={() => setView('landing')} className="absolute top-10 right-10 text-slate-500 hover:text-white transition-colors">
              <X size={40} />
            </button>

            {/* STEP 0: SÉLECTION PROFIL */}
            {step === 'selection' && (
              <div className="animate-in fade-in zoom-in-95 text-center py-10">
                <h2 className="text-5xl font-black italic uppercase mb-4">Nature de l'Agent</h2>
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-[0.4em] mb-16">Sélectionnez votre cadre juridique</p>
                <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
                  <ProfileCard 
                    icon={<User size={40}/>} title="Personnel" 
                    desc="Individu, Commerçant de proximité, Consultant indépendant." 
                    onClick={() => { setAccountType('INDIVIDUEL'); setStep('kyc_data'); }} 
                  />
                  <ProfileCard 
                    icon={<Building2 size={40}/>} title="Entreprise" 
                    desc="SARL, SAS, GIE. Requiert RCCM et IFU valides." 
                    onClick={() => { setAccountType('ENTREPRISE'); setStep('kyc_data'); }} 
                  />
                </div>
              </div>
            )}

            {/* STEP 1: KYC DATA */}
            {step === 'kyc_data' && (
              <div className="animate-in slide-in-from-right-8">
                <header className="mb-12">
                  <h3 className="text-4xl font-black italic uppercase text-emerald-500">01. Dossier Civil</h3>
                  <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mt-2">Informations obligatoires pour la Police et l'Assurance</p>
                </header>
                <div className="grid md:grid-cols-2 gap-8">
                  <InputBox label="Nom & Prénom Gérant" placeholder="Identité complète" onChange={v => updateForm('nom_gerant', v)} />
                  <InputBox label="N° CNIB / Passeport" placeholder="Référence officielle" onChange={v => updateForm('cni_numero', v)} />
                  {accountType === 'ENTREPRISE' && (
                    <>
                      <InputBox label="Raison Sociale" placeholder="Nom de l'entité" onChange={v => updateForm('raison_sociale', v)} />
                      <div className="grid grid-cols-2 gap-4">
                        <InputBox label="RCCM" placeholder="N° Commerce" onChange={v => updateForm('rccm', v)} />
                        <InputBox label="IFU" placeholder="N° Fiscal" onChange={v => updateForm('ifu', v)} />
                      </div>
                    </>
                  )}
                  <div className="md:col-span-2 grid grid-cols-3 gap-6 pt-6 border-t border-white/5">
                    <InputBox label="Ville / Quartier" placeholder="Ex: Ouaga, Pissy" onChange={v => updateForm('ville', v)} />
                    <InputBox label="Adresse / Repère" placeholder="Ex: Face Boulangerie" onChange={v => updateForm('quartier', v)} />
                    <InputBox label="Lien Google Maps" placeholder="URL GPS de la boutique" onChange={v => updateForm('gps_link', v)} />
                  </div>
                </div>
                <div className="mt-12 flex gap-4">
                  <button onClick={() => setStep('selection')} className="px-10 py-6 bg-white/5 rounded-2xl font-black text-[10px] uppercase">Retour</button>
                  <button onClick={() => setStep('kyc_docs')} className="flex-1 py-6 bg-emerald-500 text-black rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl">Suivant : Pièces Jointes</button>
                </div>
              </div>
            )}

            {/* STEP 2: KYC DOCS */}
            {step === 'kyc_docs' && (
              <div className="animate-in slide-in-from-right-8">
                <header className="mb-12 text-center md:text-left">
                  <h3 className="text-4xl font-black italic uppercase text-emerald-500">02. Pièces Justificatives</h3>
                  <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mt-2">Scans certifiés requis (Format Image/PDF)</p>
                </header>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  <FileUploader label="Photo 4x4 Gérant" icon={<Camera size={20}/>} />
                  <FileUploader label="Scan CNIB / PASS" icon={<FileText size={20}/>} />
                  <FileUploader label="Facture ONEA / SONABEL" icon={<MapPin size={20}/>} />
                  {accountType === 'ENTREPRISE' && (
                    <>
                      <FileUploader label="Scan RCCM" icon={<Building2 size={20}/>} />
                      <FileUploader label="Certificat IFU" icon={<Landmark size={20}/>} />
                    </>
                  )}
                  <FileUploader label="Justificatif Adresse" icon={<MapPin size={20}/>} />
                </div>
                <div className="mt-12 flex gap-4">
                  <button onClick={() => setStep('kyc_data')} className="px-10 py-6 bg-white/5 rounded-2xl font-black text-[10px] uppercase">Retour</button>
                  <button onClick={() => setStep('roi_sim')} className="flex-1 py-6 bg-emerald-500 text-black rounded-2xl font-black uppercase text-[10px] tracking-widest">Suivant : Business Plan</button>
                </div>
              </div>
            )}

            {/* STEP 3: ROI SIMULATOR */}
            {step === 'roi_sim' && (
              <div className="animate-in slide-in-from-right-8">
                <InvestorROI grade={formData.grade} setGrade={(g) => updateForm('grade', g)} />
                <div className="mt-12 flex gap-4">
                  <button onClick={() => setStep('kyc_docs')} className="px-10 py-6 bg-white/5 rounded-2xl font-black text-[10px] uppercase">Retour</button>
                  <button onClick={() => setStep('oath')} className="flex-1 py-6 bg-emerald-500 text-black rounded-2xl font-black uppercase text-[10px] tracking-widest">Signer le Serment d'Honneur</button>
                </div>
              </div>
            )}

            {/* STEP 4: OATH & FULL CONTRACT */}
            {step === 'oath' && (
              <div className="animate-in zoom-in-95 max-w-3xl mx-auto">
                {/* Visualiseur de Contrat Intégré */}
                <div className="mb-8">
                  {!showFullContract ? (
                    <button onClick={() => setShowFullContract(true)} className="w-full p-6 bg-white/5 border border-dashed border-white/20 rounded-3xl flex items-center justify-between group">
                      <div className="flex items-center gap-4">
                        <ScrollText className="text-emerald-500" />
                        <span className="text-[10px] font-black uppercase tracking-widest">Lire le Contrat Intégral (2026.03.19)</span>
                      </div>
                      <ChevronRight className="group-hover:translate-x-1 transition-transform" />
                    </button>
                  ) : (
                    <div className="relative">
                      <div className="bg-[#0f0f0f] p-8 rounded-3xl border border-white/10 text-[10px] font-bold uppercase leading-relaxed max-h-[400px] overflow-y-auto no-scrollbar text-slate-400">
                        <h4 className="text-emerald-500 text-lg font-black mb-4">CONTRAT D'AGRÉMENT SAHELGUARD</h4>
                        <p className="mb-4 text-white">ARTICLE 1 : CODE D'HONNEUR - L'Agent s'interdit toute complicité de vol. Sanction : déchéance immédiate et poursuites pénales.</p>
                        <p className="mb-4">ARTICLE 2 : COMMISSIONS - Directe 15% | Passif 1% | Bonus CA 1%.</p>
                        <p className="mb-4 text-rose-500 font-black italic">ARTICLE 7 : PRODUCTIVITÉ - AMBASSADEUR (250k CA) / GOUVERNEUR (500k CA). Rétrogradation après 3 mois d'échec.</p>
                        <p className="mb-4">ENGAGEMENT : En payant, vous acceptez la radiation définitive sans remboursement en cas de faute morale.</p>
                      </div>
                      <button onClick={() => setShowFullContract(false)} className="mt-4 text-emerald-500 text-[9px] font-black uppercase underline">Réduire le texte</button>
                    </div>
                  )}
                </div>

                <HonorOath 
                  grade={formData.grade} 
                  onSigned={() => setStep('payment')} 
                  onBack={() => setStep('roi_sim')} 
                />
              </div>
            )}

            {/* STEP 5: PAIEMENT */}
            {step === 'payment' && (
              <div className="max-w-md mx-auto text-center py-10 animate-in fade-in">
                <CreditCard size={60} className="text-emerald-500 mx-auto mb-8" />
                <h3 className="text-4xl font-black italic uppercase text-emerald-500">PawaPay Secure</h3>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-10 italic">Validation du pacte par cautionnement</p>
                <div className="bg-white/5 p-8 rounded-[3rem] border border-white/10 mb-8">
                    <div className="flex justify-between mb-6 text-[11px] font-black uppercase">
                      <span className="text-slate-500">Grade : {formData.grade}</span>
                      <span>{GRADES[formData.grade].caution.toLocaleString()} FCFA</span>
                    </div>
                    <input type="tel" placeholder="Numéro Mobile Money" className="w-full bg-black border border-white/10 p-5 rounded-2xl text-xl font-mono outline-none focus:border-emerald-500 mb-4" />
                    <button onClick={() => setStep('success')} className="w-full py-6 bg-emerald-500 text-black font-black uppercase tracking-widest text-[11px] rounded-2xl">Payer Maintenant</button>
                </div>
                <div className="flex justify-center gap-6 opacity-40 grayscale h-6">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/a/a9/Orange_logo.svg" alt="Orange" />
                  <img src="https://upload.wikimedia.org/wikipedia/commons/b/b8/Moov_Africa_Logo.png" alt="Moov" />
                </div>
              </div>
            )}

            {/* STEP 6: SUCCESS */}
            {step === 'success' && (
              <div className="py-20 text-center animate-in fade-in">
                <div className="w-32 h-32 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-10 shadow-[0_0_60px_rgba(16,185,129,0.4)]">
                   <CheckCircle2 size={60} className="text-black" />
                </div>
                <h3 className="text-5xl font-black italic uppercase mb-6">Pacte Scellé</h3>
                <p className="text-slate-400 max-w-lg mx-auto text-[11px] font-bold leading-relaxed uppercase tracking-widest mb-12">
                  Bienvenue Gardien. Votre caution a été séquestrée. Votre terminal sera activé après audit de vos pièces KYC (max 24h).
                </p>
                <button onClick={() => setView('landing')} className="px-16 py-6 bg-white/5 border border-white/10 rounded-full font-black uppercase text-[10px] tracking-widest hover:bg-emerald-500 hover:text-black transition-all">Retour au QG</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* FOOTER */}
      <footer className="py-10 text-center opacity-30">
        <p className="text-[10px] font-black uppercase tracking-[0.5em]">SahelGuard Sovereign Cloud Infrastructure © 2026</p>
      </footer>

      <style>{`
        .animate-in { animation: fadeIn 0.6s ease-out; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .no-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
};

// --- COMPOSANTS DE STRUCTURE ---

const ProfileCard = ({ icon, title, desc, onClick }) => (
  <button onClick={onClick} className="group p-10 bg-white/5 border border-white/10 rounded-[3rem] hover:border-emerald-500 transition-all text-left">
    <div className="text-emerald-500 mb-6 group-hover:scale-110 transition-transform">{icon}</div>
    <h4 className="text-2xl font-black uppercase italic">{title}</h4>
    <p className="text-[10px] text-slate-500 mt-2 font-bold uppercase leading-relaxed">{desc}</p>
  </button>
);

const InvestorROI = ({ grade, setGrade }) => {
  const [caPerso, setCaPerso] = useState(500000);
  const [caFilleuls, setCaFilleuls] = useState(2000000);
  
  const totalCash = (caPerso * 0.15) + (caFilleuls * 0.01) + (caPerso >= 100000 ? caPerso * 0.01 : 0);
  const pointsVoyage = (caPerso * 0.01) / 200000;

  return (
    <div className="space-y-10">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {['LEGIONNAIRE', 'POINT FOCAL', 'AMBASSADEUR', 'GOUVERNEUR'].map(g => (
          <button key={g} onClick={() => setGrade(g)} className={`p-6 rounded-3xl border transition-all text-left ${grade === g ? 'bg-emerald-500 text-black border-emerald-500' : 'bg-white/5 border-white/10'}`}>
            <h5 className="text-[9px] font-black uppercase mb-1">{g}</h5>
            <div className="text-lg font-black italic">{g === 'LEGIONNAIRE' ? '100k' : g === 'POINT FOCAL' ? '250k' : g === 'AMBASSADEUR' ? '500k' : '1.5M'}</div>
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-10 bg-white/[0.02] border border-white/10 p-10 rounded-[3rem]">
        <div className="space-y-8">
          <InputRange label="Votre CA Personnel" val={caPerso} min={100000} max={2000000} onChange={setCaPerso} unit="CFA" />
          <InputRange label="CA de votre Réseau" val={caFilleuls} min={0} max={10000000} onChange={setCaFilleuls} unit="CFA" />
        </div>
        <div className="space-y-4">
          <div className="p-8 bg-emerald-500 text-black rounded-[2.5rem]">
             <div className="text-[9px] font-black uppercase opacity-60">Estimation Revenu Mensuel</div>
             <div className="text-5xl font-black italic tracking-tighter">{totalCash.toLocaleString()} <span className="text-sm">CFA</span></div>
          </div>
          <div className="p-6 bg-white/5 border border-white/10 rounded-[2.5rem] flex justify-between items-center">
             <div>
                <div className="text-[9px] font-black uppercase text-slate-500">Points Voyage</div>
                <div className="text-xl font-black">{pointsVoyage.toFixed(2)} PTS</div>
             </div>
             <Plane className="text-emerald-500 opacity-20" size={30} />
          </div>
        </div>
      </div>
    </div>
  );
};

const HonorOath = ({ grade, onSigned, onBack }) => {
  const [agreed, setAgreed] = useState({ h: false, p: false, l: false });
  const isReady = agreed.h && agreed.p && agreed.l;

  return (
    <div className="bg-[#080808] p-10 border border-emerald-500/20 rounded-[3rem] shadow-2xl">
      <div className="text-center mb-10">
        <Gavel className="text-emerald-500 mx-auto mb-4" size={40} />
        <h3 className="text-3xl font-black italic uppercase">Serment du Gardien</h3>
      </div>
      <div className="space-y-4">
        <label className="flex items-start gap-4 p-6 bg-white/5 border border-white/10 rounded-3xl cursor-pointer hover:border-emerald-500/30 transition-all">
          <input type="checkbox" checked={agreed.h} onChange={() => setAgreed({...agreed, h: !agreed.h})} className="w-6 h-6 mt-1 accent-emerald-500" />
          <span className="text-[10px] font-black uppercase leading-relaxed text-slate-300">
            Je m'engage à une <span className="text-white">probite absolue</span>. Toute complicité de vol ou fraude KYC entraîne ma radiation sans remboursement.
          </span>
        </label>
        
        <label className="flex items-start gap-4 p-6 bg-white/5 border border-white/10 rounded-3xl cursor-pointer hover:border-emerald-500/30 transition-all">
          <input type="checkbox" checked={agreed.p} onChange={() => setAgreed({...agreed, p: !agreed.p})} className="w-6 h-6 mt-1 accent-emerald-500" />
          <span className="text-[10px] font-black uppercase leading-relaxed text-slate-300">
            {grade === 'AMBASSADEUR' || grade === 'GOUVERNEUR' ? 
              `Je comprends l'obligation de CA minimum (${grade === 'GOUVERNEUR' ? '500.000' : '250.000'} CFA) sous peine de rétrogradation (Art. 7).` :
              "Je m'engage à fournir un service de scellage rigoureux et une assistance 24/7 à mes clients."}
          </span>
        </label>

        <label className="flex items-start gap-4 p-6 bg-white/5 border border-white/10 rounded-3xl cursor-pointer hover:border-emerald-500/30 transition-all">
          <input type="checkbox" checked={agreed.l} onChange={() => setAgreed({...agreed, l: !agreed.l})} className="w-6 h-6 mt-1 accent-emerald-500" />
          <span className="text-[10px] font-black uppercase leading-relaxed text-slate-300">
            J'accepte le contrat SahelGuard 2026.03.19 et reconnais que le paiement de la caution vaut signature numérique irrévocable.
          </span>
        </label>
      </div>
      <div className="mt-10 flex gap-4">
        <button onClick={onBack} className="px-8 py-5 bg-white/5 rounded-2xl font-black text-[9px] uppercase">Retour</button>
        <button disabled={!isReady} onClick={onSigned} className={`flex-1 py-5 rounded-2xl font-black uppercase text-[10px] tracking-widest flex items-center justify-center gap-3 transition-all ${isReady ? 'bg-emerald-500 text-black' : 'bg-white/5 text-slate-600 cursor-not-allowed'}`}>
          <PenTool size={16} /> Signer & Payer la Caution
        </button>
      </div>
    </div>
  );
};

const InputBox = ({ label, placeholder, onChange }) => (
  <div className="space-y-2">
    <label className="text-[9px] font-black uppercase text-slate-500 ml-1">{label}</label>
    <input required placeholder={placeholder} onChange={(e) => onChange(e.target.value)} 
      className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 font-bold text-sm outline-none focus:border-emerald-500 transition-all text-white placeholder:opacity-20" />
  </div>
);

const InputRange = ({ label, val, min, max, onChange, unit }) => (
  <div className="space-y-4">
    <div className="flex justify-between text-[9px] font-black uppercase">
      <span className="text-slate-500">{label}</span>
      <span className="text-white">{val.toLocaleString()} {unit}</span>
    </div>
    <input type="range" min={min} max={max} step={50000} value={val} onChange={(e) => onChange(parseInt(e.target.value))}
      className="w-full h-1 bg-white/10 rounded-lg appearance-none cursor-pointer accent-emerald-500" />
  </div>
);

const FileUploader = ({ label, icon }) => {
  const [done, setDone] = useState(false);
  return (
    <div onClick={() => setDone(!done)} className={`p-6 rounded-3xl border transition-all cursor-pointer text-center flex flex-col items-center gap-2 ${done ? 'border-emerald-500 bg-emerald-500/5' : 'border-white/5 bg-white/[0.02]'}`}>
      <div className={done ? 'text-emerald-500' : 'text-slate-500'}>{done ? <CheckCircle2 size={20}/> : icon}</div>
      <h5 className="text-[9px] font-black uppercase leading-tight">{label}</h5>
    </div>
  );
};

export default SahelGuardPortal;